/**
 * The two moments the page exists for, as events rather than as states.
 *
 * A breach and an all-clear are the only things that happen on this site which a *snapshot*
 * cannot express. Every other fact — how many zombies, how many clicks, whether a provider is
 * down — is a value in the store that anything can read at any time. These two are transitions:
 * `bc` going from 2 to 3 is a gate coming off its hinges, and the same `bc` of 3 read a second
 * later is just a number. A subscriber that arrives late must see nothing; one that is there must
 * see it exactly once.
 *
 * So they live here, in a small emitter, and not in `LiveState`. Putting a `breaching: true` flag
 * in the state would mean choosing a duration for it inside the reducer, re-rendering every
 * consumer twice for one event, and — the bug this actually avoids — replaying the breach on
 * every reconnect, because `hello` re-delivers a `bc` the page has already celebrated.
 *
 * **The store is the only producer.** Not the scene: the canvas is lazily loaded and may never
 * arrive at all (no WebGL, a crashed GPU process, a `prefers-reduced-motion` poster), and the
 * screen-reader announcement, the document title and the BREACHED! stamp are not allowed to
 * depend on whether a GPU was available. The scene is one more subscriber, and it is the one that
 * turns these into a camera shake and a red flash (`render/homeScene.ts`).
 */

import {
  isProviderId,
  PROVIDER_IDS,
  type ProviderId,
  type ResetRecord,
  type ServerMsg,
} from '@/net/transport'
import { pageNow } from '@/util/pageClock'
import type { LiveState } from './live'

export interface BreachMoment {
  provider: ProviderId
  /** `bc` after the breach — the number the "BREACHES ×n" badge should pop to. */
  breaches: number
}

/**
 * The bill for a siege, which only the `allclear` frame carries: the store folds that frame into
 * a peaceful square and would otherwise throw these three numbers away (`docs/api.md` § 1.2).
 */
export interface AllClearMoment {
  provider: ProviderId
  clicks: number
  breaches: number
  durationS: number
  /** The siege's final v2 state (`docs/api.md` § 1.2, `allclear.walls`); `null` from a v1 server. */
  walls: { w: number; z: number } | null
}

/**
 * A wall came down (F28, R-119). The fourth moment, and the first with a *duration* the page has
 * to agree on: the stamp, the shake, the charge through the rubble and the sound are one event
 * seen by four subscribers, and a subscriber that arrives late — React mounting after a `?t=`
 * fast-forward, the canvas mounting after the fall — reads `at` and joins the moment where it is
 * rather than replaying it from the start. `at` is `pageNow()`, which under `?demo=` is the
 * simulator's own clock, so a frozen screenshot at `t` shows the frame the moment is on at `t`.
 */
export interface WallMoment {
  provider: ProviderId
  sid: string
  /** Fallen walls after this fall, 1–3. Three is the overrun. */
  w: number
  /** The v2 count when the wall fell. */
  z: number
  /** The outbound post for this wall when one was made at the moment of publishing (R-120). */
  post: string | null
  /** `pageNow()` when the fall was announced. Ages are measured from it. */
  at: number
  /** The server's own fall time, and the first wall's, for "4 h 20 min after the first fall". */
  fellAt: string | null
  firstFellAt: string | null
}

/** The third fall, with the bill: how many it took and how long since the first wall went. */
export interface OverrunMoment {
  provider: ProviderId
  sid: string
  z: number
  at: number
  /** Seconds between the first wall's fall and this one, or `null` when the server dated neither. */
  sinceFirstS: number | null
  post: string | null
}

/**
 * How long each fall owns its square, in seconds, by the wall that fell (task F28 § 2): each one
 * recognisably larger than the last, the overrun the largest. The scene, the stamp and the
 * recovery of a moment in flight all read this one table.
 */
export const WALL_MOMENT_S: readonly number[] = [1.5, 2.5, 4]

export function wallMomentDuration(w: number): number {
  return WALL_MOMENT_S[Math.max(0, Math.min(WALL_MOMENT_S.length - 1, Math.trunc(w) - 1))]!
}

/**
 * OpenAI relented: the counter goes to zero and the page gets to say what it cost.
 *
 * The third moment, and the only one that is not about a wall. Like the other two it cannot be
 * read out of a snapshot — `reset.c` sitting at 41 is a counter, and `reset.c` *becoming* 0 is the
 * thing the site exists to celebrate — and like the other two it must fire exactly once for a
 * subscriber that was watching and never for one that arrived afterwards.
 */
export interface CodexResetMoment {
  /** The reset's id, or a synthetic one when only the counter drop was seen. See `resetFrom`. */
  id: string
  kind: ResetRecord['kind']
  /** How many presses it took. `null` only when the drop was inferred and nothing said. */
  clicksTaken: number | null
  summary: string
  url: string | null
  at: string
}

type Listener<T> = (moment: T) => void

const breachListeners = new Set<Listener<BreachMoment>>()
const allClearListeners = new Set<Listener<AllClearMoment>>()
const codexResetListeners = new Set<Listener<CodexResetMoment>>()
const wallListeners = new Set<Listener<WallMoment>>()
const overrunListeners = new Set<Listener<OverrunMoment>>()

/**
 * The last fall announced per square — the whole of the wall moments' idempotence rule.
 *
 * A fall arrives twice by design: the `wall` event, and the tick within 500 ms whose `walls.w` has
 * already gone up (`docs/api.md` § 1.2). Playing both would stamp WALL 1 DOWN twice and shake the
 * square twice, so a moment fires once per `(sid, w)`, whichever evidence lands first. A reconnect
 * `hello` is deliberately not a path: it settles the picture and announces nothing, for the same
 * reason the Codex reset ignores `reset.last`.
 */
const lastWall = new Map<ProviderId, WallMoment>()

/**
 * The last reset announced, and when — the whole of the idempotence rule.
 *
 * A reset arrives twice by design. The `reset` event is broadcast to every socket, and the tick
 * that follows within 500 ms carries an `r` that has already dropped to zero; on a reconnect the
 * new `hello` carries the same reset in `reset.last` again. Celebrating any of those a second
 * time would stamp "IT TOOK 0 CLICKS" over the real number, so a moment is emitted once per id —
 * and a counter drop with no id of its own is suppressed when a real one just fired.
 */
let lastCodexReset: { id: string; at: number } | null = null

/**
 * The reset counter as the last **tick** reported it, and the reason this is not read off the
 * store.
 *
 * A tick's `r` and an ack's `r` are both authoritative and they are sampled at different moments:
 * the ack is the value Valkey returned for *this* batch, the tick is the value a periodic snapshot
 * read, possibly a moment earlier. So while somebody is holding the MUST HAVE RESET button the two
 * interleave and the store's `resetClicks` goes 1000 (tick) → 1020 (ack) → 1000 (tick) as a matter
 * of routine. Comparing the store before and after a frame read that as the counter going *down*,
 * which is the one thing this file treats as a reset — and a visitor who was only clicking was
 * told "RESET GRANTED", which is what the owner's user reported (2026-09-09).
 *
 * Comparing tick to tick removes the race rather than thresholding it: two ticks come from the
 * same snapshot loop on the same socket, over an `INCRBY` that only goes up, so a decrease between
 * them is the counter genuinely having been put back — a reset, or the admin repair that is the
 * only other thing allowed to move it that way.
 *
 * `null` means "nothing to compare against yet": before the first tick, and after every `hello`,
 * because a reset that happened while the tab was disconnected is history and announcing it on
 * reconnect is exactly the lie `reset.last` is kept out of this file to avoid.
 */
let lastTickReset: number | null = null

/**
 * How long an id-less counter drop is attributed to the reset that was just announced.
 *
 * Generous on purpose: the two evidence paths are milliseconds apart in practice, and the cost of
 * being too generous is a missed celebration for a second reset inside twenty seconds — which the
 * server's own six-hour dedupe (`docs/architecture/codex-resets.md`) says cannot happen — while
 * the cost of being too tight is the double stamp above.
 */
const RESET_DEDUPE_MS = 20_000

export function onBreach(listener: Listener<BreachMoment>): () => void {
  breachListeners.add(listener)
  return () => {
    breachListeners.delete(listener)
  }
}

export function onAllClear(listener: Listener<AllClearMoment>): () => void {
  allClearListeners.add(listener)
  return () => {
    allClearListeners.delete(listener)
  }
}

export function onCodexReset(listener: Listener<CodexResetMoment>): () => void {
  codexResetListeners.add(listener)
  return () => {
    codexResetListeners.delete(listener)
  }
}

export function onWall(listener: Listener<WallMoment>): () => void {
  wallListeners.add(listener)
  return () => {
    wallListeners.delete(listener)
  }
}

export function onOverrun(listener: Listener<OverrunMoment>): () => void {
  overrunListeners.add(listener)
  return () => {
    overrunListeners.delete(listener)
  }
}

/**
 * The fall this square is still in the middle of, or `null`.
 *
 * For whoever mounts after the event: the `/v2` overlay after a `?t=` fast-forward, the aerial
 * scene once Pixi has initialised. Both would otherwise draw the settled aftermath and miss the
 * frame the moment exists for. Measured against `pageNow()`, so the answer at `?freeze=1&t=3.6`
 * is "0.6 s into wall 1's fall" on every machine.
 */
export function currentWallMoment(provider: ProviderId, now: number = pageNow()): WallMoment | null {
  const moment = lastWall.get(provider)
  if (!moment) return null
  return wallMomentAge(moment, now) < wallMomentDuration(moment.w) ? moment : null
}

/** Seconds since the fall was announced, never negative. */
export function wallMomentAge(moment: WallMoment, now: number = pageNow()): number {
  return Math.max(0, (now - moment.at) / 1000)
}

/**
 * Emit, tolerating a listener that throws.
 *
 * One overlay failing to render must not cost the screen-reader announcement or the favicon
 * swap: they are separate subscribers to the same event, and a listener list is not a pipeline.
 */
function emit<T>(listeners: Set<Listener<T>>, moment: T): void {
  for (const listener of [...listeners]) {
    try {
      listener(moment)
    } catch (error) {
      console.warn('[moment] listener failed', error)
    }
  }
}

/** Test seam, and the counterpart to `resetLiveStore`. */
export function resetSiegeEvents(): void {
  breachListeners.clear()
  allClearListeners.clear()
  codexResetListeners.clear()
  wallListeners.clear()
  overrunListeners.clear()
  lastCodexReset = null
  lastTickReset = null
  lastWall.clear()
}

/**
 * Compare the state before and after one frame and announce what happened in between.
 *
 * Two guards, and each is the difference between a payoff and a lie:
 *
 *   - **Nothing before `hello`.** A page opened onto a siege that was breached an hour ago
 *     arrives with `bc` already at 4. That is history, not an event; stamping BREACHED! on it
 *     would claim the wall came down the moment the visitor looked at it. Only a `bc` that rises
 *     while the page is watching a siege it has already seen is a breach.
 *   - **Same `sid`.** A new siege starts its own `bc` at zero and a finished one drops to zero;
 *     neither is a breach, and comparing the numbers alone cannot tell the difference.
 *
 * The all-clear is read off the frame rather than off the state difference for the same reason
 * the payload exists: `clicks`, `breaches` and `durationS` describe a siege that no longer has a
 * square to be read from by the time this runs.
 */
export function announceMoments(before: LiveState, after: LiveState, msg: ServerMsg): void {
  // Before the `helloReceived` guard, because the guard's whole point is that a `hello` announces
  // nothing — and forgetting the counter is not an announcement, it is what stops the *next* tick
  // announcing a reset that happened while this tab was away.
  if (msg.t === 'hello') lastTickReset = null
  if (!before.helloReceived) return

  announceCodexReset(after, msg)

  if (msg.t === 'allclear' && isProviderId(msg.p) && before.squares[msg.p].phase === 'siege') {
    const walls = msg.walls
    emit(allClearListeners, {
      provider: msg.p,
      clicks: finite(msg.clicks),
      breaches: finite(msg.breaches),
      durationS: finite(msg.durationS),
      walls:
        walls && typeof walls === 'object'
          ? { w: Math.max(0, Math.min(3, Math.trunc(finite(walls.w)))), z: finite(walls.z) }
          : null,
    })
    return
  }

  announceWalls(before, after, msg)

  for (const id of PROVIDER_IDS) {
    const was = before.squares[id]
    const now = after.squares[id]
    if (now.sid === null || now.sid !== was.sid) continue
    if (now.breaches > was.breaches) emit(breachListeners, { provider: id, breaches: now.breaches })
  }
}

/**
 * The reset, from whichever evidence arrives first.
 *
 * Two paths, because the contract offers two and neither is guaranteed to be the one that lands:
 * the `reset` event, which carries the id and the click count, and a `tick` whose `r` has dropped,
 * which `docs/api.md` § 1.2 says to treat as the same news ("use whichever comes first,
 * idempotently by `reset.id`"). The drop path exists for the socket that missed the event — a
 * frame dropped on a slow reconnect — and it can still name the number, because the counter the
 * page held a moment ago *is* how many clicks it took.
 *
 * The drop is measured **tick against tick** and never against the store, which mixes ticks with
 * acks and made an ordinary click batch look like a reset; see {@link lastTickReset}.
 *
 * `hello` is deliberately not a path. A tab that reconnects an hour later gets the same reset in
 * `reset.last`, and stamping a celebration on it would claim OpenAI relented the instant the
 * socket came back.
 */
function announceCodexReset(after: LiveState, msg: ServerMsg): void {
  if (msg.t === 'reset') {
    const at = typeof msg.at === 'string' ? msg.at : new Date().toISOString()
    emitCodexReset({
      id: msg.id,
      kind: msg.kind,
      clicksTaken: finiteOrNull(msg.clicksTaken),
      summary: typeof msg.summary === 'string' ? msg.summary : '',
      url: typeof msg.url === 'string' ? msg.url : null,
      at,
    })
    // The tick that follows carries the zeroed counter. Recording it here means that tick is not
    // also read as a drop — the dedupe window would have caught it, but only by spending the
    // window, and a second genuine reset inside it would then be the one that went unannounced.
    lastTickReset = after.resetClicks
    return
  }

  if (msg.t !== 'tick') return

  const seen = lastTickReset
  lastTickReset = after.resetClicks
  if (seen === null || after.resetClicks >= seen) return

  emitCodexReset({
    // Synthetic, and derived from the counter it was inferred from: two drops to the same number
    // inside the dedupe window are the same news arriving twice.
    id: `drop:${seen}`,
    kind: after.lastReset?.kind ?? 'hard',
    clicksTaken: seen,
    summary: '',
    url: null,
    at: new Date().toISOString(),
  })
}

/**
 * A wall fall, from whichever evidence arrives first: the `wall` event, or a tick whose `w` rose
 * while the page was watching the same siege. Both read the *after* state for `fell` and `post`,
 * which the reducer has already appended; the event's own `post` wins for the wall it names,
 * because a tick carries neither. A tick that jumps `w` by two (a missed event) is one moment,
 * for the wall it lands on: two stamps at once would be two claims about one instant.
 */
function announceWalls(before: LiveState, after: LiveState, msg: ServerMsg): void {
  if (msg.t === 'wall') {
    if (!isProviderId(msg.p)) return
    const square = after.squares[msg.p]
    if (square.phase !== 'siege' || square.sid === null || square.walls === null) return
    const w = Math.max(0, Math.min(3, Math.trunc(finite(msg.w))))
    if (w < 1) return
    emitWall(msg.p, square.sid, w, finite(msg.z), typeof msg.post === 'string' ? msg.post : null, square.walls.fell)
    return
  }
  if (msg.t !== 'tick') return
  for (const id of PROVIDER_IDS) {
    const was = before.squares[id]
    const now = after.squares[id]
    if (now.sid === null || now.sid !== was.sid || now.walls === null) continue
    const from = was.walls?.w ?? 0
    if (now.walls.w <= from) continue
    const w = now.walls.w
    emitWall(id, now.sid, w, now.walls.z, now.walls.post[w - 1] ?? null, now.walls.fell)
  }
}

function emitWall(
  provider: ProviderId,
  sid: string,
  w: number,
  z: number,
  post: string | null,
  fell: readonly string[],
): void {
  const last = lastWall.get(provider)
  if (last && last.sid === sid && last.w >= w) return
  const moment: WallMoment = {
    provider,
    sid,
    w,
    z,
    post,
    at: pageNow(),
    fellAt: fell[w - 1] || null,
    firstFellAt: fell[0] || null,
  }
  lastWall.set(provider, moment)
  emit(wallListeners, moment)
  if (w < 3) return
  emit(overrunListeners, {
    provider,
    sid,
    z,
    at: moment.at,
    sinceFirstS: secondsBetween(moment.firstFellAt, moment.fellAt),
    post,
  })
}

function secondsBetween(fromIso: string | null, toIso: string | null): number | null {
  if (fromIso === null || toIso === null) return null
  const from = Date.parse(fromIso)
  const to = Date.parse(toIso)
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null
  return Math.round((to - from) / 1000)
}

function emitCodexReset(moment: CodexResetMoment): void {
  const now = Date.now()
  if (lastCodexReset !== null) {
    if (lastCodexReset.id === moment.id) return
    // A different id inside the window is the other evidence path for the same reset: the event
    // and the counter drop that follows it. One reset, one celebration.
    if (now - lastCodexReset.at < RESET_DEDUPE_MS) return
  }
  lastCodexReset = { id: moment.id, at: now }
  emit(codexResetListeners, moment)
}

function finiteOrNull(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.trunc(value) : null
}

function finite(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0
}
